import type { ColumnDef } from '@tanstack/react-table';

import { Skeleton } from '@connected/ui/components/skeleton';
import { cn } from '@connected/ui/lib/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table';

import type { Observation, ObservationObjectKinds, ObservationParentKinds } from '../utils/observation-types';

import { DeleteObservationButton } from './observation-delete-button';
import { SingleExistingObservation } from './observation-edit-modal';
import { ObservationSeverityIndicator } from './observation-severity-indicator';

export type ObservationTableProps = {
    electricalProjectVersionId: string;
    objectKind: ObservationObjectKinds;
    parentKind: ObservationParentKinds;
    observations: Observation[];
    isLoading: boolean;
    className?: string;
};

export function ObservationTable({
    electricalProjectVersionId,
    objectKind,
    parentKind,
    observations,
    isLoading,
    className,
}: ObservationTableProps) {
    const { t } = useTranslation('re-inspections');

    const columns = useMemo<ColumnDef<Observation>[]>(() => ([
        {
            accessorKey: 'Observation',
            header: t('observations.table.columns.observation'),
            cell: ({ row }) => (
                <ObservationSeverityIndicator
                    reports={row.original.Reports}
                    fallbackCodeKind={row.original.CodeKind}
                    variant="labeled"
                    className="ms-2"
                />
            ),
        },
        {
            accessorKey: 'Location',
            header: t('observations.table.columns.location'),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row gap-2 justify-between line-clamp-1 truncate">
                        <p className="p-1 line-clamp-1 truncate">
                            {row.original.LocationRemark || 'Geen locatie'}
                        </p>
                    </div>
                );
            },
        },
        {
            accessorKey: 'Actions',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <SingleExistingObservation
                        item={row.original}
                        electricalProjectVersionId={electricalProjectVersionId}
                        objectKind={objectKind}
                        parentKind={parentKind}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'DeleteButton',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <DeleteObservationButton
                        item={row.original}
                        electricalProjectVersionId={electricalProjectVersionId}
                    />
                </div>
            ),
        },
    ]), [electricalProjectVersionId, objectKind, parentKind, t]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => (
                            <Skeleton className="h-8 w-full" />
                        ),
                    }))
                : columns,
        [columns, isLoading],
    );

    if (observations.length === 0) {
        return null;
    }

    return (
        <DataTable
            className={cn('', className)}
            columns={tableColumns}
            data={observations}
            cellClassName={cell => cn({
                // Observation column: flexible width, takes remaining space, allows truncation
                'min-w-0 overflow-hidden': cell.column.id === 'Observation',
                // Location column: fixed width
                'w-48 min-w-48': cell.column.id === 'Location',
                // Actions column: minimum content width (as small as possible)
                'w-auto min-w-fit': cell.column.id === 'Actions',
                // DeleteButton column: fixed width
                'w-16 min-w-16': cell.column.id === 'DeleteButton',
            })}
        />
    );
}
